// The boat's notebook for the Equipment page (TOM-610, cloud ADR 0370): the
// read, read again after every change, and the note a change has just written.

import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchBoatNotebook, type BoatNote } from './boatNotes';

export type BoatNotebookLoad =
  | { kind: 'loading' }
  | { kind: 'ready'; notes: BoatNote[] }
  | { kind: 'error' };

export interface UseBoatNotebook {
  load: BoatNotebookLoad;
  /** Read again. */
  refresh: () => void;
  /** Take a note the server answered with, in place, or at the end when it is new. */
  apply: (note: BoatNote) => void;
  /** Take a note off the page (after a forget). */
  drop: (noteId: string) => void;
}

export function useBoatNotebook(boatId: string): UseBoatNotebook {
  const [load, setLoad] = useState<BoatNotebookLoad>({ kind: 'loading' });
  // A read answers only if nothing newer started since, so a slow read cannot
  // put back what a change just replaced.
  const seqRef = useRef(0);

  const refresh = useCallback(() => {
    if (boatId === '') return;
    const seq = ++seqRef.current;
    void fetchBoatNotebook(boatId).then(
      (notes) => {
        if (seq === seqRef.current) setLoad({ kind: 'ready', notes });
      },
      () => {
        if (seq !== seqRef.current) return;
        // A failed refresh keeps the notes already shown.
        setLoad((prev) => (prev.kind === 'ready' ? prev : { kind: 'error' }));
      }
    );
  }, [boatId]);

  useEffect(() => {
    setLoad({ kind: 'loading' });
    refresh();
  }, [refresh]);

  const apply = useCallback((note: BoatNote) => {
    seqRef.current++;
    setLoad((prev) =>
      prev.kind !== 'ready'
        ? prev
        : {
            kind: 'ready',
            notes: prev.notes.some((n) => n.id === note.id)
              ? prev.notes.map((n) => (n.id === note.id ? note : n))
              : [...prev.notes, note],
          }
    );
  }, []);

  const drop = useCallback((noteId: string) => {
    seqRef.current++;
    setLoad((prev) =>
      prev.kind !== 'ready'
        ? prev
        : { kind: 'ready', notes: prev.notes.filter((n) => n.id !== noteId) }
    );
  }, []);

  return { load, refresh, apply, drop };
}
